import React, { useState, useEffect } from 'react';
import { RadioGroup as CustomRadioGroup } from '@headlessui/react';
import { useDataContext } from '../../context/data';
import translations from '../../constants/translations.json';
import { compact } from 'lodash';

// Resolve a stable identity for an option, falling back to the name when no
// `_id`/`id` exists — otherwise every option's `undefined` id compares equal and
// selecting one highlights them all.
const getOptionKey = (option) => option?.id ?? option?._id ?? option?.name ?? null;

export function RadioGroup({
  options = [],
  defaultSelected = null,
  onChange,
  currency,
  required = false,
  selectedOption = null,
  connectedAttributes = [],
  onConnectedAttributeChange,
  selectedAttributes = [],
}) {
  const [selected, setSelected] = useState(selectedOption || defaultSelected);

  const { language } = useDataContext();

  // Update selected option when selectedOption changes (when editing different items)
  useEffect(() => {
    if (selectedOption) {
      // Find matching option from the available options
      const matchedOption = options.find(
        (option) =>
          getOptionKey(option) === getOptionKey(selectedOption) ||
          (selectedOption.options &&
            selectedOption.options.some((opt) => getOptionKey(opt) === getOptionKey(option))),
      );

      if (matchedOption) {
        setSelected(matchedOption);
      }
    } else if (selectedOption === null) {
      // If explicitly set to null (for clearing selection)
      setSelected(null);
    }
  }, [selectedOption, options]);

  const handleSelection = (newOption) => {
    // If clicking on already selected option and not required, unselect it
    if (!required && selected && getOptionKey(selected) === getOptionKey(newOption)) {
      setSelected(null);
      if (onChange) onChange(null);
    } else {
      // Otherwise select the new option
      setSelected(newOption);
      if (onChange) onChange(newOption);
    }
  };

  const handleClearSelection = (e, option) => {
    // Stop the event from propagating to parent (which would select the option)
    e.stopPropagation();

    if (!required && selected && getOptionKey(selected) === getOptionKey(option)) {
      setSelected(null);
      if (onChange) onChange(null);
    }
  };

  // Helper function to check if an option is currently selected
  const isOptionSelected = (option) => {
    if (!selected) return false;

    const optionKey = getOptionKey(option);

    // If selected is an array (for multi-select components)
    if (Array.isArray(selected)) {
      // Check if any option in the selected array matches this option
      return selected.some(
        (item) =>
          (item.options && item.options.some((opt) => getOptionKey(opt) === optionKey)) ||
          getOptionKey(item) === optionKey,
      );
    }

    // For single selection
    return getOptionKey(selected) === optionKey;
  };

  return (
    <>
      <CustomRadioGroup value={selected} onChange={handleSelection}>
        <div className="w-full space-y-2 mx-auto">
          {options.map((option, index) => (
            <CustomRadioGroup.Option
              key={getOptionKey(option) ?? index}
              value={option}
              className={({ checked }) => `
              flex items-center justify-between px-3 py-[12px] rounded-full cursor-pointer transition border
              ${
                isOptionSelected(option)
                  ? 'bg-secondary text-white border-secondary hover:border-white'
                  : 'bg-white border-[#f4f4f4] hover:!border-primary'
              }
            `}
              onClick={(e) => {
                if (!required && isOptionSelected(option)) {
                  handleClearSelection(e, option);
                }
              }}
            >
              {({ checked }) => (
                <>
                  <span
                    className={`text-xs font-semibold ${
                      isOptionSelected(option) ? 'text-white' : 'text-black'
                    }`}
                  >
                    {option.name}
                  </span>

                  <div className="flex items-center space-x-2">
                    <span
                      className={`text-xs font-semibold ${
                        isOptionSelected(option) ? 'text-white' : 'text-black'
                      }`}
                    >
                      {option.priceLabel ||
                        `${option.currency || currency} ${option.price || option.Price || 0}`}
                    </span>

                    <div
                      className={`w-6 h-6 flex items-center justify-center rounded-full ${
                        isOptionSelected(option) ? 'bg-white text-secondary' : 'bg-[#f4f4f4]'
                      }`}
                    >
                      {isOptionSelected(option) && (
                        <span className="w-[12px] h-[12px] rounded-full bg-secondary" />
                      )}
                    </div>
                  </div>
                </>
              )}
            </CustomRadioGroup.Option>
          ))}
        </div>
      </CustomRadioGroup>

      {connectedAttributes
        ?.filter((connectedAttr) =>
          selected?.isconnectedto?.some((item2) => item2 === connectedAttr._id),
        )
        .map((connectedAttr) => {
          const connectedAttrSelection = selectedAttributes.find(
            (attr) => attr.attributeId === connectedAttr._id,
          );
          const connectedSelectedOptions =
            compact(connectedAttrSelection?.options)?.map((item3) => ({
              ...item3,
              id: item3._id,
            })) || [];
          const isRequired = connectedAttr.attributesRequired;

          return (
            <div key={connectedAttr._id} className="mt-2 p-3 bg-gray-100 rounded-md">
              <h3 className="font-medium mb-2">
                {connectedAttr.name}
                {isRequired && (
                  <span className="text-sm text-red-500 ml-1">
                    &#40;{translations[language]?.restaurant_menu_required}&#41;
                  </span>
                )}
              </h3>
              <RadioGroup
                options={connectedAttr.options.map((item3) => ({
                  ...item3,
                  id: item3._id,
                }))}
                onChange={(selectedOption) =>
                  onConnectedAttributeChange?.(
                    connectedAttr._id,
                    selectedOption,
                    selectedOption?.menuid || '',
                  )
                }
                currency={currency}
                required={connectedAttr.attributesRequired}
                selectedOption={
                  connectedSelectedOptions.length > 0 ? connectedSelectedOptions[0] : null
                }
              />
            </div>
          );
        })}
    </>
  );
}
